export enum DocumentSource {
    Codes = "codes",
    /** Every contract-scope part at once; what the selector stores when all parts are ticked. */
    ContractDocs = "contract-docs",
    Drawings = "contract-docs:drawings",
    Specs = "contract-docs:specs",
    Rfis = "contract-docs:rfis",
    Schedules = "contract-docs:schedules",
    Supplemental = "contract-docs:supplemental",
    Documents = "category:documents",
    IFOA = "ifoa",
    PrimeContract = "primecontract",
    Contracts = "contracts",
    Submittals = "submittals",
}

/** The words for a source, read by the entry's filter labels without the selector's rules. */
const SOURCE_LABELS: Record<DocumentSource, string> = {
    [DocumentSource.ContractDocs]: "Drawings · Specs · RFIs",
    [DocumentSource.Drawings]: "Drawings",
    [DocumentSource.Specs]: "Specs",
    [DocumentSource.Rfis]: "RFIs",
    [DocumentSource.Schedules]: "Schedules",
    [DocumentSource.Supplemental]: "Supplemental",
    [DocumentSource.Documents]: "Documents",
    [DocumentSource.Contracts]: "Contracts",
    [DocumentSource.Codes]: "Building Codes",
    [DocumentSource.IFOA]: "IFOA",
    [DocumentSource.PrimeContract]: "Prime Contract",
    [DocumentSource.Submittals]: "Submittals",
};

export const documentSourceLabel = (source: DocumentSource) => SOURCE_LABELS[source];

/** The parts of the contract-documents scope, each its own checkbox, in menu order.
 *  ai_engine's `CONTRACT_SCOPE_PARTS` maps each to its document types. */
export const CONTRACT_PART_SOURCES = [
    DocumentSource.Drawings,
    DocumentSource.Specs,
    DocumentSource.Rfis,
    DocumentSource.Schedules,
    DocumentSource.Supplemental,
] as const;

export type ContractPartSource = (typeof CONTRACT_PART_SOURCES)[number];

const CONTRACT_PART_SET = new Set<string>(CONTRACT_PART_SOURCES);

export function isContractPartSource(token: string): token is ContractPartSource {
    return CONTRACT_PART_SET.has(token);
}

const SHARED_FILTER_CATEGORY_SOURCES = [
    "category:drawing",
    "category:specification",
    "category:schedule",
    "category:project_management",
] as const;

export type FilterCategorySource = (typeof SHARED_FILTER_CATEGORY_SOURCES)[number];
/** An area (`area:<uuid>`): it narrows the other sources to that area, by the server's one area
 *  rule; several are their union, and none means every area. */
export type AreaToken = `area:${string}`;
export type FolderToken = `folder:${string}`;
export type SourceToken = DocumentSource | FilterCategorySource | FolderToken | AreaToken;

const NAMED_SOURCES = new Set<string>(Object.values(DocumentSource));
const FILTER_CATEGORY_SOURCES = new Set<string>(SHARED_FILTER_CATEGORY_SOURCES);

/** Prefix for a folder-scoped source/search token — shared with the `scope=`
 *  grammar in `shared/searchScopes.ts`, which means the same thing there. */
export const FOLDER_TOKEN_PREFIX = "folder:";

export function folderToken(folderId: string): FolderToken {
    return `${FOLDER_TOKEN_PREFIX}${folderId}`;
}

/** Prefix for an area token, beside the folder one (ai_engine `SourceScopeTokens.area_ids`). */
export const AREA_TOKEN_PREFIX = "area:";

export function areaToken(areaId: string): AreaToken {
    return `${AREA_TOKEN_PREFIX}${areaId}`;
}

export function isAreaToken(token: string): token is AreaToken {
    return token.startsWith(AREA_TOKEN_PREFIX);
}

/** Parse Marvin source tokens arriving outside TypeScript's type system. Search
 * categories other than `category:documents` are deliberately rejected: the
 * web DocumentSourceSelector cannot edit conversations carrying them. */
export function parseSourceTokens(raw: unknown): SourceToken[] {
    if (!Array.isArray(raw)) return [];
    return raw.filter(
        (token): token is SourceToken =>
            typeof token === "string" &&
            (NAMED_SOURCES.has(token) ||
                token.startsWith(FOLDER_TOKEN_PREFIX) ||
                isAreaToken(token)),
    );
}

export function isFilterCategorySource(token: string): token is FilterCategorySource {
    return FILTER_CATEGORY_SOURCES.has(token);
}

export function isFolderToken(token: SourceToken): token is FolderToken {
    return token.startsWith(FOLDER_TOKEN_PREFIX);
}

/** True when both source-token lists hold exactly the same tokens, in any order. */
export function sameSources(a: SourceToken[], b: SourceToken[]): boolean {
    return a.length === b.length && a.every((token) => b.includes(token));
}
